import { Layers, ShieldCheck, BarChart3, AlertTriangle, Sparkles } from 'lucide-react'
import { useSeo } from '@/lib/useSeo'
import { PageHeader, Prose } from '@/components/public/PageHeader'
import { UsviMap } from '@/components/public/UsviMap'
import { MetaPhaseLink } from '@/components/public/MetaPhaseLink'

const fastFacts = [
  { label: 'USVI ports', value: '3', sub: 'St. Thomas · St. John · St. Croix' },
  { label: 'Entries / year', value: '~28,000', sub: 'currently keyed by hand' },
]

const beliefs = [
  {
    Icon: Layers,
    title: 'Meet the data where it lives',
    body: 'Importers and brokers already prepare the data needed to complete CBP Form 7501. VICE starts from that data, with no added burden.',
  },
  {
    Icon: ShieldCheck,
    title: 'Compliance is structural',
    body: 'Row level security, encrypted data, audit ready transmissions, and Section 508 / WCAG 2.1 AA are built in from the start.',
  },
  {
    Icon: BarChart3,
    title: 'Better data drives better decisions',
    body: 'Line level data unlocks risk targeting, Virgin Islands Deposit Fund monitoring, and reporting that simply is not possible today.',
  },
]

export function About() {
  useSeo({
    title: 'About VICE',
    description:
      'VICE helps close the visibility gap between U.S. Virgin Islands ports and ACE with clearer entry data and better operational review.',
  })
  return (
    <>
      <PageHeader
        eyebrow="About"
        title="Entry summary visibility, purpose built for the USVI."
        lede="Limited access to line level entry summary data from U.S. Virgin Islands ports reduces visibility in ACE. This impacts risk targeting, revenue protection, and reporting. VICE works to close that gap."
      />

      {/* Map + fast facts */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid gap-10 lg:gap-16 lg:grid-cols-2 items-center">
        <div>
          <UsviMap className="w-full max-w-xl mx-auto h-auto" />
        </div>
        <div>
          <div className="text-sm font-semibold uppercase tracking-wider text-accent-sunset">
            Three islands. One workflow.
          </div>
          <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-primary">
            St. Thomas, St. John, and St. Croix.
          </h2>
          <p className="mt-3 text-neutral-700 leading-relaxed">
            Goods entering the U.S. Virgin Islands are governed by territorial law. The territory
            sits outside the U.S. customs territory, and duty rates are set under Virgin Islands
            law, while CBP enforces applicable federal laws at the border.
          </p>
          <dl className="mt-6 grid grid-cols-2 gap-4">
            {fastFacts.map(({ label, value, sub }) => (
              <div key={label} className="rounded-lg border border-neutral-200 bg-neutral-50 p-5">
                <dt className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
                  {label}
                </dt>
                <dd className="mt-1 text-3xl font-bold text-primary">{value}</dd>
                <dd className="mt-0.5 text-xs text-neutral-600">{sub}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Problem ↔ Approach side by side */}
      <section className="bg-neutral-50 border-y border-neutral-200 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid gap-8 lg:grid-cols-2">
            {/* Problem */}
            <article className="bg-white rounded-lg border-l-4 border-accent-coral shadow-sm p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="inline-flex p-2 rounded bg-accent-coral/10 text-accent-coral">
                  <AlertTriangle size={20} aria-hidden="true" />
                </div>
                <h2 className="text-2xl font-bold text-primary">The problem</h2>
              </div>
              <p className="text-neutral-700 leading-relaxed">
                USVI ports process roughly <strong>28,000 entries per year</strong> manually, with
                only header-level data reaching ACE after a CBP employee keys it in.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-neutral-700">
                <li className="flex gap-2">
                  <span className="text-accent-coral flex-shrink-0">✕</span>
                  Risk is not targeted at the item level within shipments.
                </li>
                <li className="flex gap-2">
                  <span className="text-accent-coral flex-shrink-0">✕</span>
                  No economic-impact analysis
                </li>
                <li className="flex gap-2">
                  <span className="text-accent-coral flex-shrink-0">✕</span>
                  No systematic Virgin Islands Deposit Fund monitoring
                </li>
                <li className="flex gap-2">
                  <span className="text-accent-coral flex-shrink-0">✕</span>
                  Hours of repetitive keying for every entry
                </li>
              </ul>
            </article>

            {/* Approach */}
            <article className="bg-white rounded-lg border-l-4 border-accent-turquoise shadow-sm p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="inline-flex p-2 rounded bg-accent-turquoise/10 text-accent-turquoise">
                  <Sparkles size={20} aria-hidden="true" />
                </div>
                <h2 className="text-2xl font-bold text-primary">Our approach</h2>
              </div>
              <p className="text-neutral-700 leading-relaxed">
                VICE uses <strong>AI Workflow Automation</strong> to convert existing digital
                documents into structured line level data and transmit it to ACE.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-neutral-700">
                <li className="flex gap-2">
                  <span className="text-accent-turquoise flex-shrink-0">✓</span>
                  Line level entry data into ACE
                </li>
                <li className="flex gap-2">
                  <span className="text-accent-turquoise flex-shrink-0">✓</span>
                  AI-assisted extraction, validation, recommendations
                </li>
                <li className="flex gap-2">
                  <span className="text-accent-turquoise flex-shrink-0">✓</span>
                  Every transmission audit-trailed
                </li>
                <li className="flex gap-2">
                  <span className="text-accent-turquoise flex-shrink-0">✓</span>
                  Human in the loop on every submission
                </li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* Beliefs */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="max-w-2xl">
          <div className="text-sm font-semibold uppercase tracking-wider text-accent-sunset">
            How we think about this
          </div>
          <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-primary">
            Three things shape every design decision.
          </h2>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {beliefs.map(({ Icon, title, body }) => (
            <article key={title} className="bg-white rounded-lg p-6 border border-neutral-200 shadow-sm">
              <div className="inline-flex p-3 rounded bg-secondary-lightest text-secondary">
                <Icon size={22} aria-hidden="true" />
              </div>
              <h3 className="mt-4 text-lg font-bold text-primary">{title}</h3>
              <p className="mt-2 text-sm text-neutral-700 leading-relaxed">{body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Long-form */}
      <Prose>
        <h2>The legal framing</h2>
        <p>
          Under{' '}
          <a
            href="https://www.ecfr.gov/current/title-19/chapter-I/part-7/section-7.2"
            target="_blank"
            rel="noreferrer"
          >
            19 CFR § 7.2
          </a>
          , the U.S. Virgin Islands sit outside the U.S. customs territory. Imports are governed
          by territorial law, with duty rates set locally, while CBP enforces applicable federal
          laws at the border. U.S. trade agreement preferences and Section 321 de minimis
          generally do not apply to imports into the USVI.
        </p>

        <p>
          This is not a replacement for eventual full ACE automation at USVI ports. It is a bridge
          that helps close the operational gap today, while aligning USVI processes more closely
          with other CBP ports.
        </p>
      </Prose>

      {/* Built by MetaPhase */}
      <section className="bg-neutral-50 border-t border-neutral-200 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid gap-10 lg:gap-16 lg:grid-cols-5 items-center">
          <div className="lg:col-span-2 text-center lg:text-left">
            <a
              href="https://metaphase.tech"
              target="_blank"
              rel="noreferrer"
              className="inline-block focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-4 rounded"
              aria-label="Visit MetaPhase"
            >
              <img
                src="/images/metaphase-logo.png"
                alt="MetaPhase"
                className="h-14 w-auto inline-block"
              />
            </a>
          </div>
          <div className="lg:col-span-3">
            <div className="text-sm font-semibold uppercase tracking-wider text-accent-sunset">
              Who builds VICE
            </div>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-primary">
              Built by <MetaPhaseLink />.
            </h2>
            <p className="mt-3 text-neutral-700 leading-relaxed">
              <MetaPhaseLink /> delivers government-grade SaaS for federal and territorial
              agencies, with deep work in cloud engineering, cybersecurity, data &amp; analytics,
              digital platforms, and experience design. The AI underpinning VICE rides on{' '}
              <strong>ChallengeAI</strong>, MetaPhase&apos;s AI accelerator suite — assessed
              Awardable in the U.S. Department of War&apos;s Tradewinds Solutions Marketplace.
            </p>
            <p className="mt-3 text-sm text-neutral-600">
              VICE is <strong>not</strong> an official U.S. government website. It is a
              private sector tool. We do not use federal seals, logos, or official marks in the
              product.
            </p>
          </div>
        </div>
      </section>
    </>
  )
}
